import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import type { EmblaOptionsType, EmblaCarouselType } from "embla-carousel";
import clsx from "clsx";
import styles from "./Slider.module.scss";
import { SliderControls } from "./SliderControls";
import { BackgroundColor } from "@silversea-ssc/ui-components-react";
import { PrevButton } from "../PaginationButtons/PrevButton";
import { NextButton } from "../PaginationButtons/NextButton";

type SliderVariant = "normal" | "wide" | "full" | "inline" | "compact";

type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";

export type SliderProps = {
  variantColor?: BackgroundColor;
  withMask?: boolean;
  withLeftOverflow?: boolean;
  onSlideChange?: (slideNumber: number) => void;
  loop?: boolean;
  hideNextSlideGroup?: boolean;
  /**
   * Controls how slides are sized relative to their content.
   * - `"fill"` (default): slides fill the variant's track width, cards stretch to fit.
   * - `"content"`: slides shrink to the card's intrinsic max-width (480px),
   *   so the next card is always visible without empty gaps.
   */
  slideSizing?: "fill" | "content";
  /**
   * Number of visible slides at each breakpoint (mobile-first).
   * Values propagate upward: `{ xs: 1, md: 3 }` means 1 slide up to md, then 3.
   * Supports fractional values for a "peek" effect (e.g. `1.2`).
   * When provided, overrides the variant's default track sizing.
   */
  slidesPerView?: Partial<Record<Breakpoint, number>>;
  /**
   * Slide spacing in pixels at each breakpoint (mobile-first).
   * Values propagate upward: `{ xs: 16, lg: 32 }` means 16px up to lg, then 32px.
   * When provided, overrides the variant's default spacing.
   */
  slideSpacing?: Partial<Record<Breakpoint, number>>;
  emblaConfig?: Partial<EmblaOptionsType>;
  /**
   * Overrides the default width (var(--grid-width)).
   */
  sliderWidth?: string;
  /**
   * Applies white background to prev/next buttons.
   */
  whiteControls?: boolean;
} & {
  variant?: SliderVariant;
};

const SliderSlideBase: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  return <React.Fragment>{children}</React.Fragment>;
};
export const SliderSlide = React.memo(SliderSlideBase);

const emblaConfigDefault: Partial<EmblaOptionsType> = {
  align: "start",
  slidesToScroll: "auto"
};

const spvVarMap: Record<Breakpoint, string> = {
  xs: "--spv",
  sm: "--spv-sm",
  md: "--spv-md",
  lg: "--spv-lg",
  xl: "--spv-xl",
  xxl: "--spv-xxl"
};

const ssVarMap: Record<Breakpoint, string> = {
  xs: "--ss",
  sm: "--ss-sm",
  md: "--ss-md",
  lg: "--ss-lg",
  xl: "--ss-xl",
  xxl: "--ss-xxl"
};

type VariantPreset = {
  slidesPerView: Partial<Record<Breakpoint, number>>;
  slideSpacing?: Partial<Record<Breakpoint, number>>;
};

const variantPresets: Record<SliderVariant, VariantPreset> = {
  normal: { slidesPerView: { xs: 1, md: 2, lg: 3 } },
  wide: { slidesPerView: { xs: 1, md: 2 } },
  full: { slidesPerView: { xs: 1 }, slideSpacing: { xs: 24 } },
  inline: {
    slidesPerView: { xs: 1, md: 2, lg: 3 },
    slideSpacing: { xs: 24 }
  },
  compact: {
    slidesPerView: { xs: 1, sm: 2, md: 3, xl: 4 },
    slideSpacing: { xs: 24 }
  }
};

export const Slider: React.FC<PropsWithChildren<SliderProps>> = (props) => {
  const {
    children,
    variantColor = "neutral05",
    variant = "normal",
    withMask = true,
    withLeftOverflow = false,
    loop = false,
    onSlideChange,
    hideNextSlideGroup = false,
    slideSizing = "fill",
    emblaConfig,
    slidesPerView,
    slideSpacing,
    sliderWidth,
    whiteControls,
    ...otherProps
  } = props;

  const emblaOptions = useMemo<EmblaOptionsType>(() => {
    return {
      ...emblaConfigDefault,
      ...emblaConfig,
      loop,
      containScroll: "trimSnaps"
    };
  }, [loop, emblaConfig]);

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const prevSlide = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const nextSlide = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(
    (api: EmblaCarouselType) => {
      const currentIndex = api.selectedScrollSnap();
      setCurrentSlide(currentIndex);
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
      if (onSlideChange) onSlideChange(currentIndex);
    },
    [onSlideChange]
  );

  useEffect(() => {
    if (!emblaApi) return;

    const updateState = () => {
      onSelect(emblaApi);
      setTotalPages(emblaApi.scrollSnapList().length);
    };

    updateState();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", updateState);
    emblaApi.on("resize", updateState);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", updateState);
      emblaApi.off("resize", updateState);
    };
  }, [emblaApi, onSelect]);

  // Show controls only when there's actually something to scroll to
  const withControls = canScrollPrev || canScrollNext;

  const slides = useMemo(
    () =>
      React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          return (
            <div className={styles.slide} key={index}>
              {child}
            </div>
          );
        }
        return null;
      }),
    [children]
  );

  const preset = variantPresets[variant];
  const effectiveSpv = slidesPerView ?? preset.slidesPerView;
  const effectiveSs = slideSpacing ?? preset.slideSpacing;

  const containerStyle = useMemo(() => {
    const vars: Record<string, string | number> = {};
    for (const [bp, count] of Object.entries(effectiveSpv)) {
      vars[spvVarMap[bp as Breakpoint]] = count;
    }
    if (effectiveSs) {
      for (const [bp, px] of Object.entries(effectiveSs)) {
        vars[ssVarMap[bp as Breakpoint]] = `${px}px`;
      }
    }
    return vars;
  }, [effectiveSpv, effectiveSs]);

  const emblaContainerClassName = clsx(
    styles.emblaContainer,
    styles.customTrack,
    effectiveSs && styles.customSpacing
  );

  if (!slides || slides.length === 0) return null;

  return (
    <div
      className={clsx(styles.carousel, {
        [styles.carouselInline]: variant === "inline",
        [styles.carouselInlineWithControls]:
          variant === "inline" && withControls,
        [styles.withMask]: withMask,
        [styles.hideNextSlideGroup]: hideNextSlideGroup,
        [styles.fillSizing]: slideSizing === "fill",
        [styles.contentSizing]: slideSizing === "content"
      })}
      style={{ ["--slider-width" as string]: sliderWidth }}
      data-content-name="slider-controls"
      {...otherProps}
    >
      {withControls && variant === "inline" && (
        <PrevButton
          dataContentPiece="slider_previous_button"
          disabled={!canScrollPrev}
          onClick={prevSlide}
          className={clsx(styles.buttonPrevInline, {
            [styles.hidden]: !canScrollPrev
          })}
        />
      )}
      <div
        className={clsx(styles.mask, {
          [styles.leftOverflow]: withLeftOverflow
        })}
      >
        <div className={styles.embla} ref={emblaRef}>
          <div
            className={emblaContainerClassName}
            style={containerStyle as React.CSSProperties}
          >
            {slides}
          </div>
        </div>
      </div>

      {withControls && variant === "inline" && (
        <NextButton
          dataContentPiece="slider_next_button"
          disabled={!canScrollNext}
          onClick={nextSlide}
          className={clsx(styles.buttonNextInline, {
            [styles.hidden]: !canScrollNext
          })}
        />
      )}

      {withControls && variant !== "inline" && (
        <SliderControls
          variantColor={variantColor}
          currentSlideIndex={currentSlide}
          totalPages={totalPages}
          isBeginning={!canScrollPrev}
          isEnd={!canScrollNext}
          prevSlide={prevSlide}
          nextSlide={nextSlide}
          whiteControls={whiteControls}
        />
      )}
    </div>
  );
};
